import { useDebouncedEffect } from '@react-hookz/web';
import { useCallback, useMemo, useState } from 'react';

import { PaymentType } from '~context/paywall/types';
import type {
	BotAvailabilityTrialOption,
	TBotConfigExtended,
} from '~entities/bot';
import { PaymentReason, PaymentReasonType } from '~features/annals';
import { annals } from '~features/annals/hooks';
import { useModal } from '~hooks/useModal';

export const useBotPaywall = (bot: TBotConfigExtended, messages: number) => {
	const { uid, clientId, name, postConditions } = bot;

	const { isOpen, open: openModal, close: closeModal } = useModal();
	const hasConditions = !!postConditions;

	const [paymentReason, setPaymentReason] = useState<PaymentReason>();
	const [paymentReasonType, setPaymentReasonType] =
		useState<PaymentReasonType>();
	const annalsOpenStore = annals.checkout.useOpenStore();
	const annalsOpenDigitalCopySubscription =
		annals.checkout.useOpenDigitalCopySubscription();

	const openBotPaywall = useCallback(
		(params: {
			reason: PaymentReason;
			reasonType: PaymentReasonType;
			callback?: (isSuccess?: boolean) => void;
		}) => {
			setPaymentReason(params.reason);
			setPaymentReasonType(params.reasonType);
			void annalsOpenStore({
				reason: params.reason,
				reasonType: params.reasonType,
				bot_UserId: clientId,
				bot_name: name,
				type: PaymentType.BotSubscription,
			});
			void annalsOpenDigitalCopySubscription({
				bot_UserId: clientId,
				bot_name: name,
			});
			openModal({ cb: params.callback })();
		},
		// eslint-disable-next-line
		[clientId, name, openModal]
	);

	const trialOptions: Record<BotAvailabilityTrialOption['type'], number> =
		useMemo(() => {
			const baseOptionsObj = {} as Record<
				BotAvailabilityTrialOption['type'],
				number
			>;
			return hasConditions && postConditions?.trialOptions
				? postConditions.trialOptions.reduce(
						(obj, item) => ({ ...obj, [item.type]: item.value }),
						baseOptionsObj
				  )
				: baseOptionsObj;
		}, [postConditions?.trialOptions, hasConditions]);

	useDebouncedEffect(
		() => {
			if (messages >= trialOptions.messages) {
				openBotPaywall({
					reason: PaymentReason.Unknown,
					reasonType: PaymentReasonType.Chat,
				});
			}
		},
		[messages, openBotPaywall, trialOptions.messages, uid],
		300
	);

	const canSendMessages = useMemo(() => {
		if (!hasConditions) {
			return true;
		}
		return messages < trialOptions.messages;
	}, [hasConditions, messages, trialOptions]);

	return {
		isOpen,
		reason: paymentReason,
		reasonType: paymentReasonType,
		canSendMessages,
		openBotPaywall,
		closeBotPaywall: closeModal,
	};
};
